import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import { useLang } from "../i18n";

export const ContactSection = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error(t("contact.error"));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-line/80 px-4 py-3.5 text-sm placeholder:text-ink/35 focus:outline-none focus:border-terra transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-36">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="md:col-span-5"
        >
          <p className="text-terra text-xs font-bold tracking-[0.35em] uppercase mb-4">{t("contact.kicker")}</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-none mb-6">{t("contact.title")}</h2>
          <p className="text-ink/60 text-sm md:text-base leading-relaxed mb-10 max-w-md">{t("contact.subtitle")}</p>

          <div className="space-y-5 text-sm">
            <div className="flex items-center gap-4">
              <span className="w-10 h-10 border border-line flex items-center justify-center text-terra">
                <Phone className="w-4 h-4" strokeWidth={1.5} />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-ink/45">{t("contact.phoneLabel")}</p>
                <p className="font-medium" data-testid="contact-phone-value">+389 78 321 872</p>
                <p className="text-xs text-terra mt-0.5" data-testid="contact-phone-note">{t("contact.phoneNote")}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="w-10 h-10 border border-line flex items-center justify-center text-terra">
                <Mail className="w-4 h-4" strokeWidth={1.5} />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-ink/45">{t("contact.emailLabel")}</p>
                <p className="font-medium" data-testid="contact-email-value">rentcentar@gmail.com</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="w-10 h-10 border border-line flex items-center justify-center text-terra">
                <MapPin className="w-4 h-4" strokeWidth={1.5} />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-ink/45">{t("contact.locationLabel")}</p>
                <p className="font-medium">Gevgelija, North Macedonia</p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
          onSubmit={submit}
          data-testid="contact-form"
          className="md:col-span-7 border border-line/70 p-8 md:p-12 bg-sand2/40"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-[11px] uppercase tracking-widest text-ink/50 mb-2">{t("contact.name")}</label>
              <input
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t("contact.namePh")}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-widest text-ink/50 mb-2">{t("contact.email")}</label>
              <input
                data-testid="contact-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t("contact.emailPh")}
                className={inputCls}
              />
            </div>
          </div>
          <div className="mb-8">
            <label className="block text-[11px] uppercase tracking-widest text-ink/50 mb-2">{t("contact.message")}</label>
            <textarea
              data-testid="contact-message-input"
              required
              rows={6}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder={t("contact.messagePh")}
              className={`${inputCls} resize-none`}
            />
          </div>
          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={sending}
            className="inline-flex items-center gap-3 px-8 py-4 bg-terra text-sand text-xs font-bold tracking-[0.2em] uppercase hover:bg-clay transition-colors duration-300 disabled:opacity-50"
          >
            {sending ? t("contact.sending") : t("contact.send")}
            <Send className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </motion.form>
      </div>
    </section>
  );
};
